/* eslint-disable no-new-func */
import { createSelector } from 'reselect';
import IntlMessageFormat from 'intl-messageformat';
import isString from 'lodash/isString';

import { STORAGE_LANGUAGE } from 'constants/storage';
import en from 'translation/en';
import { hasTemporaryKey } from 'utils/api/auth-utils';
import { isDevelopment } from 'utils/helpers/envirioment-utils';

import { makePrefixAdder } from '../utils/type-utils';

IntlMessageFormat.__addLocaleData({
    locale: 'en',
    // eslint-disable-next-line no-restricted-syntax -- TODO: legacy function expression (baseline)
    pluralRuleFunction: function(n, ord) {
        let s = String(n).split('.'),
            i = s[0],
            v0 = !s[1],
            i10 = i.slice(-1),
            i100 = i.slice(-2);
        if (ord) return 'other';

        return v0 && i10 === 1 && i100 !== 11
            ? 'one'
            : v0 && i10 >= 2 && i10 <= 4 && (i100 < 12 || i100 > 14)
            ? 'few'
            : (v0 && i10 === 0) || (v0 && i10 >= 5 && i10 <= 9) || (v0 && i100 >= 11 && i100 <= 14)
            ? 'many'
            : 'other';
    }
});

const DEFAULT_LANGUAGE = 'en';
// English is in the main chunk; Russian and Chinese (not used by the clients now) are loaded when chosen
// (LocaleLoader). Until then the messages fall back to English.
const messages = {
    en
};
const LANGUAGE_LOADERS = {
    ru: () => import(/* webpackChunkName: "translation-ru" */ 'translation/ru'),
    zh: () => import(/* webpackChunkName: "translation-zh" */ 'translation/zh')
};
export const SUPPORTED_LANGUAGES = [DEFAULT_LANGUAGE, ...Object.keys(LANGUAGE_LOADERS)];

export const isLanguageLoaded = language => language in messages;

/** Loads the messages of a language; resolves at once for a loaded or unknown one */
export const loadLanguageMessages = language =>
    isLanguageLoaded(language) || !LANGUAGE_LOADERS[language]
        ? Promise.resolve()
        : LANGUAGE_LOADERS[language]().then(module => {
              messages[language] = module.default;
          });

/** The messages of a language, English while it is not loaded (the glossary builds its articles from them) */
export const getLanguageMessages = language => messages[language] || messages[DEFAULT_LANGUAGE];

const addPrefixTo = makePrefixAdder('locale');
const LANGUAGE_CHANGE = addPrefixTo('LANGUAGE_CHANGE');
const LANGUAGE_LOADED = addPrefixTo('LANGUAGE_LOADED');
export const types = {
    LANGUAGE_CHANGE,
    LANGUAGE_LOADED
};

const languageLoaded = language => ({ type: LANGUAGE_LOADED, language });

// eslint-disable-next-line no-restricted-syntax -- TODO: legacy function declaration (baseline)
function changeLanguage(language) {
    if (hasTemporaryKey()) {
        sessionStorage.setItem(STORAGE_LANGUAGE, language);
    } else {
        localStorage.setItem(STORAGE_LANGUAGE, language);
    }

    return {
        type: LANGUAGE_CHANGE,
        language
    };
}

export const actions = {
    changeLanguage,
    languageLoaded
};

export const languageSelector = state => state.locale.language;
// Changes when the messages of a language are loaded: the formatted messages are computed again
const messagesVersionSelector = state => state.locale.messagesVersion;

const messageSelector = (state, props) => props;

// eslint-disable-next-line no-restricted-syntax -- TODO: legacy function declaration (baseline)
function formatTemplate(template, values) {
    const substitution = new Function('values', `return \`${template}\``);
    let result;

    try {
        result = substitution(values);
    } catch (e) {
        // todo extract to helper method
        if (isDevelopment()) {
            // eslint-disable-next-line no-console
            console.error(e);
        }
        result = '';
    }

    return result;
}
/**
 * @return {String} Format message according to language and specified message
 */

const formatMessageWrapper = (lang, options) => {
    try {
        return formatMessage(lang, options);
    } catch (e) {
        try {
            return formatMessage('en', options);
        } catch (e) {
            if (isDevelopment()) {
                throw e;
            }

            return options.id;
        }
    }
};

// eslint-disable-next-line no-restricted-syntax -- TODO: legacy function declaration (baseline)
function formatMessage(lang, { id, values, options, defaultMessage, language }) {
    const derivedLanguage = language || lang;
    const translation = messages[derivedLanguage] && messages[derivedLanguage][id];

    const messagesValue = (id && translation) || defaultMessage;

    if (!isString(messagesValue) && !(messagesValue && isString(messagesValue.message))) {
        throw new Error(`Wrong template for message with id=${id}`);
    }

    const template = isString(messagesValue) ? messagesValue : messagesValue.message;
    const message = formatTemplate(template, values);

    return !!values ? new IntlMessageFormat(message, derivedLanguage, options).format(values) : message;
}
/**
 * @return {String} The title if message contains title value, otherwise `undefined`
 */

// eslint-disable-next-line no-restricted-syntax -- TODO: legacy function declaration (baseline)
function formatTitle(language, { id }) {
    const message = id && (messages[language] || {})[id];

    return message && isString(message.title) ? message.title : undefined;
}
/**
 * @return {Function} Selector that return message and title
 */

export const makeGetFormattedMessageProps = () => {
    return createSelector(
        [languageSelector, messagesVersionSelector, messageSelector],
        (lang, messagesVersion, props) => ({
            message: formatMessageWrapper(lang, props),
            title: formatTitle(lang, props)
        })
    );
};
export const makeGetFormattedMessage = () => {
    return createSelector(
        [languageSelector, messagesVersionSelector, messageSelector],
        (lang, messagesVersion, props) => formatMessageWrapper(lang, props)
    );
};
export const getFormatMessageFunction = createSelector(
    languageSelector,
    messagesVersionSelector,
    language => {
        return props => formatMessageWrapper(language, props);
    }
);
const storedLanguage =
    sessionStorage.getItem(STORAGE_LANGUAGE) ||
    localStorage.getItem(STORAGE_LANGUAGE) ||
    navigator.language ||
    navigator.userLanguage ||
    DEFAULT_LANGUAGE;
const languageWithoutGroup = storedLanguage.substr(0, 2);
const validLanguage = SUPPORTED_LANGUAGES.includes(languageWithoutGroup) ? languageWithoutGroup : DEFAULT_LANGUAGE;
const initialState = {
    language: validLanguage,
    messagesVersion: 0
};
const moduleLocale = (state = initialState, action) => {
    // eslint-disable-next-line no-restricted-syntax -- TODO: legacy forEach/switch (baseline)
    switch (action.type) {
        case LANGUAGE_CHANGE:
            return {
                ...state,
                language: action.language
            };

        case LANGUAGE_LOADED:
            return {
                ...state,
                messagesVersion: state.messagesVersion + 1
            };

        default:
            return state;
    }
};
export default moduleLocale;

const languageIsChineseSelector = state => !!state.locale && !!state.locale.language && state.locale.language === 'zh';

export const languageSelectorForRecaptcha = state => (languageIsChineseSelector(state) ? 'zh-CN' : DEFAULT_LANGUAGE);
